<script lang="ts" setup>
    import { useVuelidate } from '@vuelidate/core';
    import { createI18nMessage } from '@vuelidate/validators';
    import { customValidators } from '@/i18n/utils/i18n-validators';
    import type { RequestParameters } from '#shopware';
    import type { SelectOption } from '~/components/shared/SelectInput.vue';
    import { ApiClientError, type ApiError } from '@shopware/api-client';

    const { register } = useShopwareUser();
    const { push } = useRouter();
    const { t } = useI18n();
    const { required, minLength, maxLength, requiredIf, email } = customValidators();
    const { getSalutations } = useSalutations();
    const { hfpPushInfo } = useNotificationHelpers();
    const salutationOptions = computed<SelectOption[]>(() => [
        {
            label: t('form.chooseSalutation'),
            value: '',
            selected: true,
            disabled: true
        },
        ...getSalutations.value.map((option) => ({
            label: option.displayName,
            value: option.id,
            selected: false
        }))
    ]);

    const checkoutStep = defineModel<number>('checkoutStep');

    const invokeSubmit = async () => {
        $v.value.$touch();
        registerErrors.value = [];
        const valid = await $v.value.$validate();
        if (valid) {
            try {
                const response = await register(state);
                if (!response.active) {
                    hfpPushInfo(t('checkout.messages.checkoutSignInSuccess'));
                    await push('/');
                }
            } catch (error) {
                if (error instanceof ApiClientError) {
                    registerErrors.value = error.details.errors;
                }
            }
        }
    };

    const withI18nMessage = createI18nMessage({
        t: (key, params) => t(key, params),
        messagePath: ({ $validator }) => `validations.${$validator}`
    });

    // i18n rules
    const requiredI18n = withI18nMessage(required);
    const emailI18n = withI18nMessage(email);
    const minLengthI18n = withI18nMessage(minLength, { withArguments: true });
    const maxLengthI18n = withI18nMessage(maxLength, { withArguments: true });

    const rules = computed(() => ({
        salutationId: {
            required: requiredI18n
        },
        firstName: {
            required: requiredI18n,
            minLength: minLengthI18n(3)
        },
        lastName: {
            required: requiredI18n,
            minLength: minLengthI18n(3)
        },
        email: {
            required: requiredI18n,
            email: emailI18n
        },
        password: {
            required: requiredIf(() => {
                return !state.guest;
            }),
            minLength: minLengthI18n(8)
        },
        billingAddress: {
            street: {
                required: requiredI18n,
                minLength: minLengthI18n(3)
            },
            zipcode: {
                required: requiredI18n,
                minLength: minLengthI18n(5),
                maxLength: maxLengthI18n(5)
            },
            city: {
                required: requiredI18n
            },
            countryId: {
                required: requiredI18n
            },
            countryStateId: {},
            additionalAddressLine1: {}
        }
    }));

    const state = reactive<RequestParameters<'register'>>({
        salutationId: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        guest: true,
        billingAddress: {
            street: '',
            zipcode: '',
            city: '',
            countryId: '',
            countryStateId: '',
            additionalAddressLine1: ''
        },
        acceptedDataProtection: true,
        storefrontUrl: ''
    });

    const $v = useVuelidate(rules, state);

    const registerErrors = ref<ApiError[]>([]);
</script>
<template>
    <div class="checkout-step span-all-rows">
        <PrimaryButton
            product-button
            :button-text="t('general.back')"
            class="back-btn"
            @click="checkoutStep = 1"
        >
            <template #customIcon>
                <img
                    class="icon"
                    src="~/assets/icons/chevron-right.svg"
                    alt="back"
                    aria-hidden="true"
                />
            </template>
        </PrimaryButton>

        <form
            id="checkout-billing-address"
            name="checkout-billing-address"
            method="post"
            class="guest-form"
            @submit.prevent="invokeSubmit"
        >
            <div
                v-if="registerErrors.length"
                class="error-message"
                role="alert"
            >
                <p>{{ t('errors.message-default') }}</p>
                <ul>
                    <li
                        v-for="error in registerErrors"
                        :key="error.detail"
                    >
                        {{ t('errors.message-default-addition') }}
                    </li>
                </ul>
            </div>

            <div class="registration-container">
                <div class="registration-column input-wrapper">
                    <SelectInput
                        v-model="state.salutationId"
                        :options="salutationOptions"
                        :label="`${t('form.salutation')}`"
                        :required="true"
                        class="full-width"
                        data-testid="checkout-pi-salutation-input"
                    />

                    <span
                        v-if="$v.salutationId.$errors[0]"
                        class="select-error"
                    >
                        {{ $v.salutationId.$errors[0].$message }}
                    </span>
                </div>
                <div class="registration-column two-column input-wrapper">
                    <label for="first-name">{{ t('form.firstName') }}*</label>
                    <input
                        id="first-name"
                        v-model="state.firstName"
                        type="text"
                        required
                        name="first-name"
                        :placeholder="t('form.firstNamePlaceholder')"
                        class="full-width"
                        data-testid="checkout-pi-first-name-input"
                        @blur="$v.firstName.$touch()"
                    />
                    <span
                        v-if="$v.firstName.$errors[0]"
                        class="select-error"
                    >
                        {{ $v.firstName.$errors[0].$message }}
                    </span>
                </div>

                <div class="registration-column two-column input-wrapper">
                    <label for="last-name">{{ t('form.lastName') }}*</label>
                    <input
                        id="last-name"
                        v-model="state.lastName"
                        type="text"
                        required
                        name="last-name"
                        :placeholder="t('form.lastNamePlaceholder')"
                        class="full-width"
                        data-testid="checkout-pi-last-name-input"
                        @blur="$v.lastName.$touch()"
                    />
                    <span
                        v-if="$v.lastName.$errors[0]"
                        class="select-error"
                    >
                        {{ $v.lastName.$errors[0].$message }}
                    </span>
                </div>

                <div class="registration-column input-wrapper">
                    <label for="email-address">{{ t('form.email') }}*</label>
                    <input
                        id="email-address"
                        v-model="state.email"
                        type="email"
                        required
                        name="email-address"
                        :placeholder="t('form.emailPlaceholder')"
                        autocomplete="off"
                        class="full-width"
                        data-testid="checkout-pi-email-input"
                        @blur="$v.email.$touch()"
                    />
                    <span
                        v-if="$v.email.$errors[0]"
                        class="select-error"
                    >
                        {{ $v.email.$errors[0].$message }}
                    </span>
                </div>

                <div class="registration-column input-wrapper">
                    <label for="street-addres">{{ t('form.additionalAddressLine1') }}</label>
                    <input
                        id="additional-address"
                        v-model="state.billingAddress.additionalAddressLine1"
                        type="text"
                        name="additional-address"
                        :placeholder="t('form.additionalAddressLine1Placeholder')"
                        autocomplete="address-line2"
                        class="full-width"
                        data-testid="checkout-pi-additional-address-input"
                        @blur="$v.billingAddress.additionalAddressLine1.$touch()"
                    />
                    <span
                        v-if="$v.billingAddress.additionalAddressLine1.$errors[0]"
                        class="select-error"
                    >
                        {{ $v.billingAddress.additionalAddressLine1.$errors[0].$message }}
                    </span>
                </div>

                <div class="registration-column input-wrapper">
                    <label for="street-address"> {{ t('form.streetAddress') }}* </label>
                    <input
                        id="street-address"
                        v-model="state.billingAddress.street"
                        type="text"
                        required
                        name="street-address"
                        :placeholder="t('form.streetPlaceholder')"
                        autocomplete="street-address"
                        class="full-width"
                        data-testid="checkout-pi-street-address-input"
                        @blur="$v.billingAddress.street.$touch()"
                    />
                    <span
                        v-if="$v.billingAddress.street.$errors[0]"
                        class="select-error"
                    >
                        {{ $v.billingAddress.street.$errors[0].$message }}
                    </span>
                </div>

                <div class="registration-column two-column input-wrapper">
                    <label for="postal-code"> {{ t('form.postalCode') }}* </label>
                    <input
                        id="postal-code"
                        v-model="state.billingAddress.zipcode"
                        type="text"
                        required
                        name="postal-code"
                        :placeholder="t('form.postalCodePlaceholder')"
                        autocomplete="postal-code"
                        class="full-width"
                        data-testid="checkout-pi-zip-code-input"
                        @blur="$v.billingAddress.zipcode.$touch()"
                    />
                    <span
                        v-if="$v.billingAddress.zipcode.$errors[0]"
                        class="select-error"
                    >
                        {{ $v.billingAddress.zipcode.$errors[0].$message }}
                    </span>
                </div>

                <div class="registration-column two-column input-wrapper">
                    <label for="city">{{ t('form.city') }}*</label>
                    <input
                        id="city"
                        v-model="state.billingAddress.city"
                        type="text"
                        required
                        name="city"
                        :placeholder="t('form.cityPlaceholder')"
                        autocomplete="address-level2"
                        class="full-width"
                        data-testid="checkout-pi-city-input"
                        @blur="$v.billingAddress.city.$touch()"
                    />
                    <span
                        v-if="$v.billingAddress.city.$errors[0]"
                        class="select-error"
                    >
                        {{ $v.billingAddress.city.$errors[0].$message }}
                    </span>
                </div>
                <SharedCountryStateInput
                    v-model:country-id="state.billingAddress.countryId"
                    v-model:state-id="state.billingAddress.countryStateId"
                    :country-id-validation="$v.billingAddress.countryId"
                    class="registration-column"
                />
            </div>

            <PrimaryButton
                product-button
                data-testid="checkout-guest-continue-button"
                :button-text="t('account.order.continueGuest')"
                type="submit"
            />
        </form>
    </div>
</template>
